import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArchiveIcon,
  BookOpenIcon,
  LinkIcon,
  MoveIcon,
  UploadIcon
} from './icons';
import { useDraggableChrome } from './useDraggableChrome';
import { featureEnabled, type FeatureName } from '../config/features';
import { i18n } from '../i18n/index.ts';
import '../styles/rail-drag.css';

export type RailPanelId = 'library' | 'suggestions' | 'upload' | 'link';

// keys, not text: the labels are translated when the rail renders, so they follow the language
type RailLabelKey = 'rail.label.library' | 'rail.label.suggestions' | 'rail.label.upload' | 'rail.label.link';
type RailTitleKey = 'layout:rail.title.library' | 'layout:rail.title.suggestions' | 'layout:rail.title.upload' | 'layout:rail.title.link';

export interface RailItem {
  id: RailPanelId;
  /** Rail caption - all nouns so the column reads as places, not verbs. */
  labelKey: RailLabelKey;
  /** Panel header title for the same id, so the rail and the panel cannot disagree. */
  titleKey: RailTitleKey;
  icon: ReactNode;
  /** The rail shows the item only while this flag is on. */
  feature?: FeatureName;
}

export const RAIL_ITEMS: RailItem[] = [
  { id: 'library', labelKey: 'rail.label.library', titleKey: 'layout:rail.title.library', icon: <ArchiveIcon size={24} /> },
  { id: 'suggestions', labelKey: 'rail.label.suggestions', titleKey: 'layout:rail.title.suggestions', icon: <BookOpenIcon size={24} /> },
  { id: 'upload', labelKey: 'rail.label.upload', titleKey: 'layout:rail.title.upload', icon: <UploadIcon size={24} />, feature: 'board.upload' },
  { id: 'link', labelKey: 'rail.label.link', titleKey: 'layout:rail.title.link', icon: <LinkIcon size={24} />, feature: 'board.link' },
];

/** The items the rail draws; a flagged-off item keeps its panel code but loses its button. */
export function visibleRailItems(): RailItem[] {
  return RAIL_ITEMS.filter((item) => !item.feature || featureEnabled(item.feature));
}

export function railPanelTitle(id: RailPanelId): string {
  return i18n.t(RAIL_ITEMS.filter((item) => item.id === id)[0].titleKey);
}

export interface LeftRailProps {
  activeId?: RailPanelId;
  /** Floating card over the canvas, or a full-height column once a panel is open. */
  docked?: boolean;
  onSelect?: (id: RailPanelId) => void;
}

export function LeftRail({ activeId, docked = false, onSelect }: LeftRailProps) {
  const { t } = useTranslation('layout');
  // only the floating card moves; the docked column is anchored, and keeps the saved spot for later
  const drag = useDraggableChrome({ id: 'left-rail', enabled: !docked });

  const className = [
    docked ? 'rail rail--docked' : 'rail rail--floating',
    drag.dragging ? 'rail--dragging' : '',
  ].filter(Boolean).join(' ');

  return (
    <nav ref={drag.ref} className={className} aria-label={t('rail.navLabel')}>
      {/* hidden by shell.css when docked */}
      <button
        type="button"
        className="rail__handle"
        aria-label={t('rail.handle')}
        {...drag.handleProps}
      >
        <MoveIcon size={18} />
      </button>
      {visibleRailItems().map((item) => (
        <button
          key={item.id}
          type="button"
          className={
            item.id === activeId ? 'rail__item rail__item--active' : 'rail__item'
          }
          aria-expanded={item.id === activeId}
          aria-current={item.id === activeId ? 'true' : undefined}
          onClick={onSelect ? () => onSelect(item.id) : undefined}
        >
          <span className="rail__icon" aria-hidden={true}>{item.icon}</span>
          <span className="rail__label">{t(item.labelKey)}</span>
        </button>
      ))}
    </nav>
  );
}
